'use client';
import { useOptimistic, useTransition } from 'react';

import Container from '@/components/@base/container';
import Group from '@/components/@base/input/@shared/group';
import Label from '@/components/@base/input/@shared/label';
import CheckBox from '@/components/@base/input/check-box';
import List, { ListItem } from '@/components/@base/list';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import Props from '@/components/app/archive/components/filter/@shared/items/medal-filters/type';
import { FILTER_KEYS } from '@/components/app/archive/helpers';
import { useFilterParams } from '@/components/app/archive/helpers/hooks';
import IconTextCombo from '@/components/shared/components/icon-text-combo';

import { DATA_CY_CHECK_BOX_ITEM } from '@/constant/cypress';
import imagesList from '@/constant/images-list';
import { MEDAL_BASED_FILTERS } from '@/constant/iterable-items';
import { pageLevelLocalization } from '@/constant/localization';

const { archive: archiveLocalization } = pageLevelLocalization;

export default function MedalFilters({ initialFilters }: Props) {
  const { updateParams } = useFilterParams({ searchParams: initialFilters });
  const medalFiltersObject = {
    [FILTER_KEYS.ORIGINAL]: initialFilters[FILTER_KEYS.ORIGINAL],
    [FILTER_KEYS.FEATURED]: initialFilters[FILTER_KEYS.FEATURED],
    [FILTER_KEYS.IRANIAN]: initialFilters[FILTER_KEYS.IRANIAN],
    [FILTER_KEYS.LICENCED]: initialFilters[FILTER_KEYS.LICENCED],
  };
  const [filters, setFilters] = useOptimistic(medalFiltersObject);
  let [isPending, startTransition] = useTransition();

  const handleChange = (key: string, value: boolean) => {
    const newFilters = {
      ...filters,
      [key]: value,
    };

    startTransition(() => {
      setFilters(newFilters);
      updateParams(key, value);
    });
  };

  return (
    <>
      <IconTextCombo
        wrapperClassName='my-2'
        Icon={
          <XImage
            src={imagesList.icons.medalBasedFilter}
            alt={archiveLocalization.filter.medalBased}
          />
        }
        title={archiveLocalization.filter.medalBased}
      />
      <List data-pending={isPending ? '' : undefined}>
        {MEDAL_BASED_FILTERS.map((item) => (
          <ListItem key={item.id} className='my-2 text-[#5B5C60]'>
            <Group className='flex items-center'>
              <CheckBox
                data-cy={DATA_CY_CHECK_BOX_ITEM}
                onChange={(checked) => handleChange(item.filterKey, checked)}
                checked={filters[item.filterKey]}
                className='h-4 w-4'
              />
              <Label className='mr-4 w-full'>
                <Container center className='justify-between'>
                  <Text>{item.title}</Text> <XImage src={item.icon} alt={item.title} />
                </Container>
              </Label>
            </Group>
          </ListItem>
        ))}
      </List>
    </>
  );
}
